<template>
  <div class="min-h-screen bg-bochas-bg">
    <!-- Hero con imagen de fondo -->
    <ImageBackgroundSection
      size="default"
      :image-url="content.heroImage"
      :loading-image="heroImageLoading"
      :alt="content.heroTitle"
      :title="content.heroTitle"
      :subtitle="content.heroSubtitle"
    />

    <!-- Casa del Bochófilo -->
    <section
      class="max-w-7xl mx-auto pt-16 px-6 sm:px-6 lg:px-0 lg:py-8 sm:py-12"
    >
      <div class="section-header">
        <div class="section-header__icon">
          <IconFbochas name="casa" :size="32" color="#fff" />
        </div>
        <span class="section-header__label">{{ content.sectionLabel }}</span>
      </div>
      <template v-if="sectionVisible('alojamiento.casa')">
        <h2 class="text-3xl sm:text-3xl font-bold text-white mb-10">
          {{ content.casaTitle }}
        </h2>
        <RichTextContent
          v-if="content.casaDescription"
          :html="content.casaDescription"
          class="mb-10 text-sm leading-relaxed text-white sm:text-base"
        />

        <!-- Comodidades + galería de imágenes -->
        <RevealOnScroll
          class="alojamiento-block grid grid-cols-1 md:grid-cols-2 gap-6 mb-10"
        >
          <div>
            <h3 class="text-2xl font-semibold text-white mb-6">
              {{ content.casaFeaturesTitle }}
            </h3>
            <ul
              class="space-y-2 text-white text-sm sm:text-base list-disc list-inside"
            >
              <li v-for="(item, index) in content.casaFeatures" :key="index">
                {{ item }}
              </li>
            </ul>
          </div>
          <div
            class="alojamiento-gallery"
            role="region"
            aria-label="Galería de la Casa del Bochófilo"
            tabindex="0"
          >
            <figure
              v-for="(img, idx) in content.casaImages"
              :key="idx"
              class="alojamiento-gallery__slide"
            >
              <img
                :src="img"
                :alt="`${content.casaTitle} ${idx + 1}`"
                class="alojamiento-gallery__image aspect-video w-full h-full object-cover rounded-2xl"
                loading="lazy"
              />
              <span class="alojamiento-gallery__count">
                {{ idx + 1 }} / {{ content.casaImages.length }}
              </span>
            </figure>
          </div>
        </RevealOnScroll>

        <!-- Precios -->
        <h3 class="text-xl font-semibold text-white mb-4">
          {{ content.casaPricesTitle }}
        </h3>
        <RevealOnScroll
          :delay="80"
          class="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-4"
        >
          <div
            v-for="(precio, priceIndex) in content.preciosCasa"
            :key="priceIndex"
            class="listing-motion-card card-fbochas p-5 border-t-0 border-r-0 border-b-0 border-l-[8px] border-l-bochas-rojo"
          >
            <h4 class="text-2xl font-bold text-white mb-3">
              {{ precio.titulo }}
            </h4>
            <div class="space-y-1">
              <div
                v-for="(item, itemIndex) in precio.items"
                :key="itemIndex"
                class="flex justify-between text-base"
              >
                <span class="text-bochas-rojo">{{ item.label }}</span>
                <span class="text-white font-semibold">{{ item.valor }}</span>
              </div>
            </div>
          </div>
        </RevealOnScroll>
        <p class="text-sm text-white font-normal mb-2">
          {{ content.priceNote }}
        </p>
        <p class="text-lg text-white font-normal">{{ content.parkingPrice }}</p>
      </template>

      <template v-if="sectionVisible('alojamiento.albergue')">
        <h2 class="text-3xl sm:text-3xl font-semibold text-white mt-24 mb-10">
          {{ content.albergueTitle }}
        </h2>
        <RichTextContent
          v-if="content.albergueDescription"
          :html="content.albergueDescription"
          class="mb-10 text-sm leading-relaxed text-white sm:text-base"
        />

        <RevealOnScroll
          :delay="100"
          class="alojamiento-block grid grid-cols-1 md:grid-cols-2 gap-8 mb-10"
        >
          <div>
            <h3 class="text-2xl font-medium text-white mb-6">
              {{ content.albergueFeaturesTitle }}
            </h3>
            <ul
              class="space-y-2 text-white text-sm font-normal sm:text-base list-disc list-inside"
            >
              <li
                v-for="(item, index) in content.albergueFeatures"
                :key="index"
              >
                {{ item }}
              </li>
            </ul>
          </div>
          <div
            class="alojamiento-gallery"
            role="region"
            aria-label="Galería del Albergue Estudiantil"
            tabindex="0"
          >
            <figure
              v-for="(img, idx) in content.albergueImages"
              :key="idx"
              class="alojamiento-gallery__slide"
            >
              <img
                :src="img"
                :alt="`${content.albergueTitle} ${idx + 1}`"
                class="alojamiento-gallery__image aspect-video w-full h-full object-cover rounded-2xl"
                loading="lazy"
              />
              <span class="alojamiento-gallery__count">
                {{ idx + 1 }} / {{ content.albergueImages.length }}
              </span>
            </figure>
          </div>
        </RevealOnScroll>

        <!-- Presentación de Solicitud -->
        <h3 class="text-2xl font-medium text-white mb-4">
          {{ content.applicationTitle }}
        </h3>
        <p
          class="text-sm sm:text-base text-white font-normal mb-10 leading-relaxed whitespace-pre-line"
        >
          {{ content.applicationText }}
        </p>

        <!-- Requisitos y obligaciones -->
        <h3 class="text-2xl font-medium text-white mb-4">
          {{ content.requirementsTitle }}
        </h3>
        <ul
          class="space-y-2 text-white text-sm font-normal sm:text-base list-disc list-inside mb-10"
        >
          <li v-for="(item, index) in content.requirements" :key="index">
            {{ item }}
          </li>
        </ul>

        <!-- Precios Albergue -->
        <h3 class="text-2xl font-medium text-white mb-4">
          {{ content.alberguePricesTitle }}
        </h3>
        <p class="text-sm sm:text-base text-white font-normal mb-10">
          {{ content.alberguePrice }}
        </p>
      </template>

      <!-- Contacto -->
      <RevealOnScroll
        v-if="sectionVisible('alojamiento.contacto')"
        :delay="120"
        class="alojamiento-contact card-fbochas p-6 sm:p-8 border-t-0 border-x-0 border-b-8 border-b-bochas-azul"
      >
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-4">
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-bochas-azul flex items-center justify-center flex-shrink-0"
            >
              <IconFbochas name="phoneCall" :size="20" color="white" />
            </div>
            <h3 class="text-2xl font-medium text-white">
              {{ content.contactTitle }}
            </h3>
          </div>
          <div
            class="flex items-center gap-3 sm:border-l sm:border-white/10 sm:pl-4"
          >
            <IconFbochas name="location" :size="18" color="white" />
            <div class="text-sm font-normal text-white">
              <p>{{ content.address }}</p>
              <p>{{ content.neighborhood }}</p>
              <p>{{ content.city }}</p>
            </div>
          </div>
          <div
            class="flex items-center gap-3 sm:border-l sm:border-white/10 sm:pl-4"
          >
            <IconFbochas name="phoneCall" :size="18" color="white" />
            <div class="text-sm font-normal text-white space-y-1">
              <p>{{ content.phones }}</p>
              <p>{{ content.fax }}</p>
            </div>
          </div>
        </div>
      </RevealOnScroll>

      <!-- Volver -->
      <div class="pt-24">
        <VolverButton />
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import VolverButton from "@/components/Common/VolverButton.vue";
import ImageBackgroundSection from "@/components/Common/ImageBackgroundSection.vue";
import RevealOnScroll from "@/components/Animations/RevealOnScroll.vue";
import RichTextContent from "@/components/Common/RichTextContent.vue";
import {
  crearAlojamientoContent,
  obtenerAlojamientoContent,
} from "@/services/siteSettings/alojamientoContentService";
import {
  crearSiteVisibility,
  obtenerSiteVisibility,
} from "@/services/siteSettings/siteVisibilityService";

const content = ref(crearAlojamientoContent());
const siteVisibility = ref(crearSiteVisibility());
const heroImageLoading = ref(true);
const sectionVisible = (key) => siteVisibility.value[key] !== false;
onMounted(async () => {
  try {
    const [savedContent, visibility] = await Promise.all([
      obtenerAlojamientoContent(),
      obtenerSiteVisibility(),
    ]);
    content.value = savedContent;
    siteVisibility.value = visibility;
  } catch (_) {
    // Conservar el contenido original si la API no está disponible.
  } finally {
    heroImageLoading.value = false;
  }
});
</script>

<style scoped>
.alojamiento-block,
.alojamiento-contact {
  transition: transform 340ms cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 340ms ease, border-color 340ms ease;
}

.alojamiento-block:hover,
.alojamiento-contact:hover {
  transform: translateY(-4px);
}

.alojamiento-contact:hover {
  border-bottom-color: #a52121;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.25);
}

.alojamiento-gallery__image {
  transition: transform 500ms cubic-bezier(0.22, 1, 0.36, 1), filter 400ms ease,
    box-shadow 400ms ease;
}

.alojamiento-gallery {
  display: flex;
  gap: 0.75rem;
  margin-inline: -1.5rem;
  padding: 0 1.5rem 0.75rem;
  overflow-x: auto;
  overscroll-behavior-inline: contain;
  scroll-padding-inline: 1.5rem;
  scroll-snap-type: inline mandatory;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.alojamiento-gallery::-webkit-scrollbar {
  display: none;
}

.alojamiento-gallery__slide {
  position: relative;
  flex: 0 0 86%;
  overflow: hidden;
  border-radius: 1rem;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.24);
}

.alojamiento-gallery__count {
  position: absolute;
  right: 0.75rem;
  bottom: 0.75rem;
  padding: 0.3rem 0.65rem;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 9999px;
  background: rgba(10, 14, 26, 0.66);
  color: white;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1;
  backdrop-filter: blur(12px);
}

@media (min-width: 640px) {
  .alojamiento-gallery {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
    margin-inline: 0;
    padding: 0;
    overflow: visible;
  }

  .alojamiento-gallery__slide {
    overflow: visible;
    box-shadow: none;
  }

  .alojamiento-gallery__count {
    display: none;
  }
}

.alojamiento-gallery__image:hover {
  position: relative;
  z-index: 1;
  transform: translateY(-4px) scale(1.035);
  filter: brightness(1.08);
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.3);
}

@media (prefers-reduced-motion: reduce) {
  .alojamiento-block,
  .alojamiento-contact,
  .alojamiento-gallery__image {
    transition: none;
  }

  .alojamiento-block:hover,
  .alojamiento-contact:hover,
  .alojamiento-gallery__image:hover {
    transform: none;
  }
}
</style>
